import { Injectable } from '@angular/core';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  DocumentSnapshot,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';

import {
  FirmwareMetadataStore,
  FirmwareMetadataEntry,
  FirmwareWithMetadata,
  FirmwareMetadataInput,
  FirmwareType,
  getFirmwareKey,
  mapSolarmanFirmware,
} from './firmware-metadata.model';
import { UpgradeService } from '../../communication/upgrade.service';
import { FIRMWARE_PRODUCT_IDS } from './firmware-metadata.utils';

@Injectable({ providedIn: 'root' })
export class FirmwareMetadataService {
  // Single document path (v2 - new collection for product-based loading)
  private readonly docPath = 'firmware-metadata-v2/all';
  private firestore = getFirestore(getApp());

  // In-memory cache of the entire metadata store
  private metadataCache: FirmwareMetadataStore | null = null;
  private cacheTimestamp = 0;
  private readonly cacheDuration = 1000 * 60 * 5; // 5 minutes

  constructor(private upgradeService: UpgradeService) {}

  /**
   * Load firmware for all known products from Solarman API in parallel
   */
  async loadAllProducts(): Promise<FirmwareWithMetadata[]> {
    const results = await Promise.all(
      FIRMWARE_PRODUCT_IDS.map(async (product) => {
        const firmwareList = await this.upgradeService.getProductFirmwareList(product.id);
        return firmwareList.map(fw => mapSolarmanFirmware(fw, product.id, product.type));
      })
    );
    return results.flat();
  }

  /**
   * Filter firmware list by product ID
   */
  filterByProduct(
    firmwareList: FirmwareWithMetadata[],
    productId: string | 'all'
  ): FirmwareWithMetadata[] {
    if (productId === 'all') {
      return firmwareList;
    }
    return firmwareList.filter(fw => fw.productId === productId);
  }

  /**
   * Load all metadata from Firestore (single read)
   */
  async loadAllMetadata(forceRefresh = false): Promise<FirmwareMetadataStore> {
    const now = Date.now();

    // Return cached if valid
    if (!forceRefresh && this.metadataCache && (now - this.cacheTimestamp) < this.cacheDuration) {
      return this.metadataCache;
    }

    const docRef = doc(this.firestore, this.docPath);
    const snapshot = await getDoc(docRef) as DocumentSnapshot<FirmwareMetadataStore>;

    this.metadataCache = snapshot.exists() ? snapshot.data() : {};
    this.cacheTimestamp = now;

    return this.metadataCache;
  }

  /**
   * Get metadata for a specific firmware
   */
  async getMetadata(firmwareKey: string): Promise<FirmwareMetadataEntry | null> {
    const store = await this.loadAllMetadata();
    return store[firmwareKey] ?? null;
  }

  /**
   * Get metadata map for quick lookups (already loaded)
   */
  async getMetadataMap(): Promise<FirmwareMetadataStore> {
    return this.loadAllMetadata();
  }

  /**
   * Save metadata for a firmware (merge into single document)
   */
  async saveMetadata(
    firmware: FirmwareWithMetadata,
    data: FirmwareMetadataInput
  ): Promise<void> {
    const firmwareKey = getFirmwareKey(firmware.productId, firmware.id);

    const entry: Record<string, any> = {
      // Firmware context
      solarmanId: firmware.id,
      version: firmware.version,
      name: firmware.name,
      productId: firmware.productId,
      firmwareType: firmware.firmwareType,
      // User-editable metadata
      reference: data.reference,
      tags: data.tags,
      updatedAt: Date.now(),
    };

    // Only include optional fields if defined (Firestore rejects undefined)
    if (firmware.fileSize != null) { entry.fileSize = firmware.fileSize; }
    if (data.loggerType) { entry.loggerType = data.loggerType; }
    if (data.serialPatterns?.length) { entry.serialPatterns = data.serialPatterns; }
    if (data.notes) { entry.notes = data.notes; }
    if (data.releaseDate != null) { entry.releaseDate = data.releaseDate; }

    // Update single document with merge
    const docRef = doc(this.firestore, this.docPath);
    await setDoc(docRef, { [firmwareKey]: entry }, { merge: true });

    // Update local cache
    if (this.metadataCache) {
      this.metadataCache[firmwareKey] = entry as FirmwareMetadataEntry;
    }
  }

  /**
   * Delete metadata for a firmware
   */
  async deleteMetadata(productId: string, firmwareId: number): Promise<void> {
    const firmwareKey = getFirmwareKey(productId, firmwareId);

    // Load current store
    const store = await this.loadAllMetadata(true);

    // Remove the key
    delete store[firmwareKey];

    // Overwrite entire document (no partial delete in Firestore for map keys)
    const docRef = doc(this.firestore, this.docPath);
    await setDoc(docRef, store);

    // Update cache
    this.metadataCache = store;
  }

  /**
   * Clear local cache (force next read from Firestore)
   */
  clearCache(): void {
    this.metadataCache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Filter firmware list by reference/version/name (client-side)
   */
  filterByReference(
    firmwareList: FirmwareWithMetadata[],
    searchQuery: string
  ): FirmwareWithMetadata[] {
    if (!searchQuery || searchQuery.trim() === '') {
      return firmwareList;
    }

    const query = searchQuery.toLowerCase().trim();
    return firmwareList.filter(fw => {
      const reference = fw.metadata?.reference?.toLowerCase() ?? '';
      const version = fw.version.toLowerCase();
      const name = fw.name.toLowerCase();
      return reference.includes(query) || version.includes(query) || name.includes(query);
    });
  }

  /**
   * Filter firmware list by tags
   */
  filterByTags(
    firmwareList: FirmwareWithMetadata[],
    tags: string[]
  ): FirmwareWithMetadata[] {
    if (!tags || tags.length === 0) {
      return firmwareList;
    }

    return firmwareList.filter(fw => {
      const fwTags = fw.metadata?.tags ?? [];
      return tags.some(tag => fwTags.includes(tag));
    });
  }

  /**
   * Filter firmware list by type
   */
  filterByType(
    firmwareList: FirmwareWithMetadata[],
    firmwareType: FirmwareType | 'all'
  ): FirmwareWithMetadata[] {
    if (firmwareType === 'all') {
      return firmwareList;
    }

    return firmwareList.filter(fw => fw.firmwareType === firmwareType);
  }
}
